import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  activityMembersUrl, deskActivity, deskActivityMembers, deskService,
  type ActivityBucket, type ActivityMetric, type ActivityTotals,
} from './api'
import {
  barHeights, bucketLabel, change, etToday, fmtCount, fmtDay, fmtMoney, fmtMoneyExact, fmtShortMoney, fmtSigned,
  labelStride, netChange, periodRange, quickRange, type Bucket, type Period, type QuickPick,
} from './activity'
import { memberHref } from './links'
import { HelpLink } from './HelpLink'
import { ServicePanel } from './ServicePanel'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const mono = 'font-mono'

const PERIODS: { key: Period; label: string; name: string }[] = [
  { key: 'day', label: 'Today', name: 'Today' },
  { key: 'week', label: 'This week', name: 'This week' },
  { key: 'month', label: 'This month', name: 'This month' },
  { key: 'year', label: 'This year', name: 'This year' },
  { key: 'custom', label: 'Custom range', name: 'Custom range' },
]
const TITLES: { label: string; slug: string }[] = [
  { label: 'All titles', slug: 'all' },
  { label: 'Flight Journal', slug: 'flight_journal' },
  { label: 'Model Airplane News', slug: 'model_airplane_news' },
  { label: 'RC Car Action', slug: 'rc_car_action' },
  { label: 'Die Cast X', slug: 'diecast' },
]
const TITLE_NAME: Record<string, string> = Object.fromEntries(TITLES.map((t) => [t.slug, t.label]))
const QUICK: { pick: QuickPick; label: string }[] = [
  { pick: '7d', label: 'Last 7 days' },
  { pick: '30d', label: 'Last 30 days' },
  { pick: 'quarter', label: 'Last quarter' },
  { pick: 'year', label: 'Last year' },
]
const BUCKET_WORD: Record<Bucket, string> = { day: 'day', week: 'week', month: 'month' }

type CardKey = ActivityMetric
interface Card { key: CardKey; label: string; value: string; num: string; pct: string; vs: string }

function Seg<T extends string>({ label, options, value, onPick }: { label: string; options: { key: T; label: string }[]; value: T; onPick: (k: T) => void }) {
  return (
    <span role="radiogroup" aria-label={label} className="inline-flex flex-wrap self-start rounded-[10px] bg-ink-600 p-[3px]">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={o.key === value}
          onClick={() => onPick(o.key)}
          className={`min-h-[38px] rounded-lg px-3.5 text-[13px] font-semibold ${focus} ${o.key === value ? 'bg-ink-700 text-paper ring-1 ring-ink-400' : 'text-paper/70 hover:text-paper'}`}
        >
          {o.label}
        </button>
      ))}
    </span>
  )
}

function Panel({ children, className = '', ...rest }: React.HTMLAttributes<HTMLElement> & { 'aria-labelledby'?: string }) {
  return <section {...rest} className={`flex min-w-0 flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 px-[22px] py-5 ${className}`}>{children}</section>
}
const H2 = ({ id, children }: { id?: string; children: React.ReactNode }) => (
  <h2 id={id} className="m-0 text-[12px] font-bold uppercase tracking-[0.1em] text-paper/60">{children}</h2>
)

function cardsFor(t: ActivityTotals, c: ActivityTotals | null, vsLabel: string): Card[] {
  const net = netChange(t)
  const cnet = c ? netChange(c) : 0
  const mk = (key: CardKey, label: string, value: string, cur: number, prev: number | null, money = false): Card => {
    if (prev === null) return { key, label, value, num: '', pct: '', vs: '' }
    const ch = change(cur, prev, money)
    return { key, label, value, num: ch.number, pct: ch.pct, vs: `vs ${vsLabel}` }
  }
  return [
    mk('new', 'New members', fmtCount(t.new), t.new, c ? c.new : null),
    mk('renewed', 'Renewed', fmtCount(t.renewed), t.renewed, c ? c.renewed : null),
    mk('lapsed', 'Lapsed', fmtCount(t.lapsed), t.lapsed, c ? c.lapsed : null),
    mk('cancelled', 'Cancelled', fmtCount(t.cancelled), t.cancelled, c ? c.cancelled : null),
    mk('net', 'Net change', fmtSigned(net), net, c ? cnet : null),
    mk('keyed', 'Payments keyed by staff', fmtCount(t.keyed), t.keyed, c ? c.keyed : null),
    mk('revenue', 'Revenue', fmtMoney(t.revenue_cents), t.revenue_cents, c ? c.revenue_cents : null, true),
  ]
}

function Axis({ buckets, bucket, i, last, withYear }: { buckets: ActivityBucket[]; bucket: Bucket; i: number; last: number; withYear: boolean }) {
  const show = i % labelStride(buckets.length) === 0 || i === last
  return <span className="mt-1.5 h-[14px] whitespace-nowrap text-[11px] text-paper/60">{show ? bucketLabel(buckets[i].start, bucket, withYear) : ''}</span>
}

export function ActivityPage() {
  const today = useMemo(() => etToday(), [])
  const [period, setPeriod] = useState<Period>('week')
  const [title, setTitle] = useState('all')
  const [custom, setCustom] = useState(() => quickRange('30d', today))
  const [drill, setDrill] = useState<CardKey | null>(null)
  const [more, setMore] = useState<{ ctx: string; n: number }>({ ctx: '', n: 50 })

  const ctx = `${drill}|${period}|${custom.from}|${custom.to}|${title}`
  const shown = more.ctx === ctx ? more.n : 50
  const customOk = !!custom.from && !!custom.to && custom.from <= custom.to
  const r = useMemo(() => periodRange(period, today, custom), [period, today, custom])
  const enabled = period !== 'custom' || customOk

  const { data, isError } = useQuery({
    queryKey: ['desk-activity', r.from, r.to, r.bucket, title, r.compareFrom, r.compareTo],
    queryFn: () => deskActivity({ from: r.from, to: r.to, bucket: r.bucket, publication: title, compare_from: r.compareFrom, compare_to: r.compareTo }),
    enabled,
    placeholderData: keepPreviousData,
  })
  // The charts span more than the cards for day, week and month; the same query key is shared when they match.
  const { data: trend } = useQuery({
    queryKey: ['desk-activity', r.trendFrom, r.trendTo, r.trendBucket, title, '', ''],
    queryFn: () => deskActivity({ from: r.trendFrom, to: r.trendTo, bucket: r.trendBucket, publication: title }),
    enabled,
    placeholderData: keepPreviousData,
  })

  const { data: service } = useQuery({
    queryKey: ['desk-service', r.from, r.to, title, r.compareFrom, r.compareTo],
    queryFn: () => deskService({ from: r.from, to: r.to, publication: title, compare_from: r.compareFrom, compare_to: r.compareTo }),
    enabled,
    placeholderData: keepPreviousData,
  })
  const per1000 = (slug: string): number | null | undefined =>
    service ? (slug === 'all' ? service.all_titles_per_1000 : service.by_title.find((b) => b.publication_slug === slug)?.per_1000 ?? null) : undefined

  const { data: drillData, isFetching: drillBusy } = useQuery({
    queryKey: ['desk-activity-members', drill, r.from, r.to, title, shown],
    queryFn: () => deskActivityMembers(drill!, { from: r.from, to: r.to, publication: title }, shown),
    enabled: enabled && drill !== null,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (drill === null) return
    const t = setTimeout(() => document.getElementById('drill')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
    return () => clearTimeout(t)
  }, [drill])

  const periodName = period === 'custom' ? `${fmtDay(r.from)} – ${fmtDay(r.to)}` : period === 'year' ? `This year, ${fmtDay(r.from)} – ${fmtDay(r.to)}` : period === 'day' ? `Today, ${fmtDay(today)}` : PERIODS.find((p) => p.key === period)!.name
  const cards = data ? cardsFor(data.totals, data.compare_totals, r.compareLabel) : []

  const tb = trend?.buckets ?? []
  const tLast = tb.length - 1
  const tBucket = r.trendBucket
  const withYear = tBucket === 'month' && r.trendFrom.slice(0, 4) !== r.trendTo.slice(0, 4)
  const trendSpan = tb.length === 0 ? '' : period === 'custom' ? `${tb.length} ${tBucket === 'week' ? 'weeks' : 'days'}, by ${BUCKET_WORD[tBucket]}` : period === 'year' ? `${bucketLabel(r.trendFrom, 'month')} – ${bucketLabel(r.trendTo, 'month')} ${r.trendTo.slice(0, 4)}, by month` : `last ${tb.length} ${BUCKET_WORD[tBucket]}s`
  const rev = tb.map((b) => b.revenue_cents)
  const revTotal = rev.reduce((a, b) => a + b, 0)
  const revTop = Math.max(0, ...rev) * 1.15
  const revH = barHeights(rev.concat(revTop), 160).slice(0, rev.length)
  const ups = tb.map((b) => b.new + b.renewed)
  const downs = tb.map((b) => b.lapsed + b.cancelled)
  const maxIO = Math.max(0, ...ups, ...downs)
  const upH = barHeights(ups.concat(maxIO), 116).slice(0, ups.length)
  const dnH = barHeights(downs.concat(maxIO), 87).slice(0, downs.length)
  const dense = tb.length > 16

  const byTitle = data?.by_title ?? []
  const allRow: ActivityTotals | null = data ? data.totals : null
  const pay = data?.payments_by_method ?? []
  const payMax = Math.max(1, ...pay.map((p) => p.payments))

  const drillCard = cards.find((c) => c.key === drill)
  const drillItems = drillData?.items ?? []
  const csvUrl = drill ? activityMembersUrl(drill, { from: r.from, to: r.to, publication: title }, 'csv') : ''
  const amtHead = drill === 'lapsed' ? '' : 'Amount'

  return (
    <main className="flex min-w-0 flex-1 flex-col gap-[22px] px-4 pb-16 pt-8 md:px-12">
      <header id="activity-top" className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <h1 className="font-serif text-4xl font-medium tracking-tight">Activity</h1>
          <p className="text-paper/70">{TITLE_NAME[title]} · {periodName}</p>
          <HelpLink page="activity" />
        </div>
        <Seg label="Period" options={PERIODS} value={period} onPick={(k) => { setPeriod(k); setDrill(null) }} />
      </header>

      {period === 'custom' && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-ink-500 bg-ink-700 px-4 py-3.5">
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70">From
            <input type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className={`min-h-[40px] rounded-lg border border-ink-400 bg-ink-800 px-2.5 text-paper ${focus}`} />
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70">To
            <input type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className={`min-h-[40px] rounded-lg border border-ink-400 bg-ink-800 px-2.5 text-paper ${focus}`} />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {QUICK.map((q) => (
              <button key={q.pick} type="button" onClick={() => setCustom(quickRange(q.pick, today))} className={`inline-flex min-h-[38px] items-center rounded-[9px] border border-ink-400 bg-ink-700 px-3.5 text-[13px] font-semibold hover:bg-ink-600 ${focus}`}>{q.label}</button>
            ))}
          </div>
          <span className="ml-auto text-[12px] text-paper/60">Compared with the same number of days just before</span>
          {!customOk && <span role="alert" className="basis-full text-[13px] text-radar-red">Pick a From date that is not after the To date.</span>}
        </div>
      )}

      <Seg label="Title" options={TITLES.map((t) => ({ key: t.slug, label: t.label }))} value={title} onPick={(k) => { setTitle(k); setDrill(null) }} />

      {isError && <p role="alert" className="text-radar-red">The figures could not be loaded. Try again in a moment.</p>}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        {cards.map((c) => {
          const on = drill === c.key
          return (
            <button
              key={c.key}
              type="button"
              aria-expanded={on}
              onClick={() => setDrill(on ? null : c.key)}
              className={`flex flex-col gap-1.5 rounded-xl border bg-ink-700 p-4 text-left ${focus} ${on ? 'border-amber-glow ring-1 ring-amber-glow' : 'border-ink-500 hover:border-paper/50'}`}
            >
              <span className="text-[13px] font-semibold text-paper/70">{c.label}</span>
              <span className={`${mono} text-[28px] font-medium tracking-tight`}>{c.value}</span>
              <span className="text-[12px] text-paper/60">{c.num && <><span className="font-semibold text-paper">{c.num}</span> {c.pct} {c.vs}</>}</span>
              <span className="text-[12px] font-semibold text-amber-glow">{on ? 'Listed at the bottom ↓' : 'See who →'}</span>
            </button>
          )
        })}
      </div>

      {data && data.notes.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-paper/70">
          {data.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}

      <Panel aria-labelledby="rev-h">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <H2 id="rev-h">Revenue{trendSpan && ` · ${trendSpan}`}</H2>
          {tb.length > 0 && <span className="text-[13px] text-paper/70">{fmtMoney(revTotal)}{trendSpan && ` · ${trendSpan}`}</span>}
        </div>
        <div className="overflow-x-auto">
          <div className="flex min-w-[560px] gap-2.5">
            <div className={`flex h-[180px] flex-col justify-between text-right text-[11px] text-paper/60 ${mono}`} aria-hidden="true">
              <span>{fmtShortMoney(revTop)}</span><span>{fmtShortMoney(revTop / 2)}</span><span>$0</span>
            </div>
            <div className="flex min-w-0 flex-1 gap-1">
              {tb.map((b, i) => (
                <div key={b.start} title={`${bucketLabel(b.start, tBucket, withYear)}: ${fmtMoneyExact(b.revenue_cents)}`} className={`flex min-w-0 flex-1 flex-col items-center ${i === tLast ? '' : 'opacity-50'}`}>
                  <div className="flex h-[180px] w-full flex-col items-center justify-end gap-1 border-b border-ink-400">
                    <span className={`${mono} whitespace-nowrap text-[11px] ${dense ? 'text-[9px]' : ''}`}>{b.revenue_cents ? fmtShortMoney(b.revenue_cents) : ''}</span>
                    <div className="w-[64%] rounded-t-[3px] bg-radar-green" style={{ height: revH[i] }} />
                  </div>
                  <Axis buckets={tb} bucket={tBucket} i={i} last={tLast} withYear={withYear} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      <div className="flex flex-wrap items-start gap-[22px]">
        <Panel aria-labelledby="by-h" className="min-w-0 flex-[999_1_560px]">
          <H2 id="by-h">By title · {periodName}</H2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-[14px]">
              <thead>
                <tr className="text-right text-[11px] font-bold uppercase tracking-[0.08em] text-paper/60">
                  {['Title', 'New', 'Renewed', 'Lapsed', 'Cancelled', 'Net', 'Payments keyed', 'Revenue', 'Contacts per 1,000 members'].map((h, i) => (
                    <th key={h} scope="col" className={`border-b border-ink-500 px-2.5 py-2.5 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byTitle.map((t) => <TitleRow key={t.publication_slug} name={TITLE_NAME[t.publication_slug] ?? t.publication_slug} t={t} sel={title === t.publication_slug} per1000={per1000(t.publication_slug)} />)}
                {allRow && <TitleRow name="All titles" t={allRow} sel={title === 'all'} total per1000={per1000('all')} />}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel aria-labelledby="pay-h" className="min-w-0 flex-[1_1_270px]">
          <H2 id="pay-h">How they paid (payments)</H2>
          <p className="m-0 text-[12px] text-paper/60">Payments in the period, by method.</p>
          {pay.length === 0 && <p className="m-0 text-paper/60">No payments in this period.</p>}
          {pay.map((p) => (
            <div key={p.method} className="grid grid-cols-[84px_minmax(0,1fr)_56px] items-center gap-2.5">
              <span className="font-semibold text-paper/70">{p.method}</span>
              <div className="h-2.5 rounded-[5px] bg-ink-600"><div className="h-2.5 rounded-[5px] bg-radar-cyan" style={{ width: `${Math.round((p.payments / payMax) * 100)}%` }} /></div>
              <span className={`${mono} text-right`}>{fmtCount(p.payments)}</span>
            </div>
          ))}
        </Panel>
      </div>

      <Panel aria-labelledby="trend-h">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <H2 id="trend-h">Coming in and going out{trendSpan && ` · ${trendSpan}`}</H2>
          <div className="flex flex-wrap gap-4 text-[13px] text-paper/70">
            <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-radar-cyan" />Joined + renewed</span>
            <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-amber-glow" />Lapsed + cancelled</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="flex min-w-[560px] gap-1">
            {tb.map((b, i) => (
              <div key={b.start} title={`${bucketLabel(b.start, tBucket, withYear)}: ${ups[i]} in, ${downs[i]} out`} className={`flex min-w-0 flex-1 flex-col items-center ${i === tLast ? '' : 'opacity-50'}`}>
                <div className="flex h-[140px] w-full flex-col items-center justify-end gap-[3px]">
                  <span className={`${mono} whitespace-nowrap leading-none ${dense ? 'text-[9px]' : 'text-[11px]'}`}>{fmtCount(ups[i])}</span>
                  <div className="w-[72%] rounded-t-[3px] bg-radar-cyan" style={{ height: upH[i] }} />
                </div>
                <div className="h-px w-full bg-ink-400" />
                <div className="flex h-[110px] w-full flex-col items-center justify-start gap-[3px]">
                  <div className="w-[72%] rounded-b-[3px] bg-amber-glow" style={{ height: dnH[i] }} />
                  <span className={`${mono} whitespace-nowrap leading-none ${dense ? 'text-[9px]' : 'text-[11px]'}`}>{fmtCount(downs[i])}</span>
                </div>
                <Axis buckets={tb} bucket={tBucket} i={i} last={tLast} withYear={withYear} />
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <ServicePanel data={service} r={r} title={title} titleName={TITLE_NAME[title]} periodName={periodName}
        cancelled={data ? data.totals.cancelled : null} />

      {drill && drillCard && (
        <Panel id="drill" aria-labelledby="drill-h" className="scroll-mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 id="drill-h" className="m-0 text-[18px] font-bold">{drillCard.label}: {drillCard.value}</h2>
              <span className="text-[13px] text-paper/60">{TITLE_NAME[title]} · {periodName} · click a member to open their record</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={csvUrl} className={`inline-flex min-h-[38px] items-center gap-2 rounded-[9px] border border-ink-400 bg-ink-700 px-3.5 text-[13px] font-semibold hover:bg-ink-600 ${focus}`}>Export CSV</a>
              <a href="#activity-top" onClick={(e) => { e.preventDefault(); document.getElementById('activity-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }} className={`inline-flex min-h-[38px] items-center rounded-[9px] border border-ink-400 bg-ink-700 px-3.5 text-[13px] font-semibold hover:bg-ink-600 ${focus}`}>Back to the top</a>
              <button type="button" onClick={() => setDrill(null)} className={`inline-flex min-h-[38px] items-center rounded-[9px] border border-ink-400 bg-ink-700 px-3.5 text-[13px] font-semibold hover:bg-ink-600 ${focus}`}>Close</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div aria-busy={drillBusy} className="min-w-[760px]">
              <div className="grid grid-cols-[minmax(0,1.6fr)_90px_minmax(0,1fr)_90px_minmax(0,1.6fr)_90px] items-center gap-4 px-1 pb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-paper/60">
                <span>Member</span><span>Account</span><span>Title</span><span>Date</span><span>Detail</span><span className="text-right">{amtHead}</span>
              </div>
              {drillItems.map((d, i) => {
                const rowCls = 'grid grid-cols-[minmax(0,1.6fr)_90px_minmax(0,1fr)_90px_minmax(0,1.6fr)_90px] items-center gap-4 border-t border-ink-500 px-1 py-3'
                const cells = (
                  <>
                    <span className="font-semibold">{d.name || 'No member on file'}</span>
                    <span className={`${mono} text-[13px]`}>{d.account_number ? `#${d.account_number}` : ''}</span>
                    <span>{d.publication_name ?? ''}</span>
                    <span className="text-paper/70">{d.at ? shortDate(d.at) : ''}</span>
                    <span className="text-paper/70">{d.detail ?? ''}</span>
                    <span className={`${mono} text-right`}>{drill === 'lapsed' || d.amount_cents === null ? '' : fmtMoneyExact(d.amount_cents)}</span>
                  </>
                )
                const key = `${d.subscriber_id ?? 'none'}-${d.at ?? ''}-${i}`
                // A row with no member (an orphaned order line) has no record to open.
                return d.subscriber_id != null
                  ? <Link key={key} to={memberHref(d.subscriber_id)} className={`${rowCls} hover:bg-ink-600 ${focus}`}>{cells}</Link>
                  : <div key={key} className={rowCls}>{cells}</div>
              })}
              {drillData && drillItems.length === 0 && <p className="border-t border-ink-500 py-4 text-paper/60">No one in this period.</p>}
            </div>
          </div>
          {drillData && drillData.total > drillItems.length && (
            <div className="flex flex-wrap items-center gap-3">
              {shown < 200 && (
                <button type="button" onClick={() => setMore({ ctx, n: Math.min(200, shown + 50) })} className={`inline-flex min-h-[38px] items-center rounded-[9px] border border-ink-400 bg-ink-700 px-3.5 text-[13px] font-semibold hover:bg-ink-600 ${focus}`}>
                  Show more ({fmtCount(drillItems.length)} of {fmtCount(drillData.total)})
                </button>
              )}
              {shown >= 200 && <span className="text-[13px] text-paper/60">Showing {fmtCount(drillItems.length)} of {fmtCount(drillData.total)}. Export CSV for the full list.</span>}
            </div>
          )}
        </Panel>
      )}
    </main>
  )
}

function shortDate(at: string): string {
  const d = at.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? bucketLabel(d, 'day') : at
}

function TitleRow({ name, t, sel, total, per1000 }: { name: string; t: ActivityTotals; sel: boolean; total?: boolean; per1000?: number | null }) {
  const cell = `${mono} px-2.5 py-3 text-right ${total ? 'font-bold' : 'border-b border-ink-500'}`
  return (
    <tr className={sel ? 'bg-ink-600' : ''}>
      <td className={`px-2.5 py-3 text-left font-semibold ${total ? 'font-bold' : 'border-b border-ink-500'}`}>{name}</td>
      <td className={cell}>{fmtCount(t.new)}</td>
      <td className={cell}>{fmtCount(t.renewed)}</td>
      <td className={cell}>{fmtCount(t.lapsed)}</td>
      <td className={cell}>{fmtCount(t.cancelled)}</td>
      <td className={cell}>{fmtSigned(netChange(t))}</td>
      <td className={cell}>{fmtCount(t.keyed)}</td>
      <td className={cell}>{fmtMoney(t.revenue_cents)}</td>
      <td className={cell}>{per1000 == null ? '—' : per1000.toFixed(1)}</td>
    </tr>
  )
}
